import { AuthContext } from '@repo-level/core-front';
import { BonanzaContext } from 'contexts/bonanza';
import { ConfigContext } from 'contexts/config';
import { StardustRushContext } from 'contexts/stardustRush/stardustRush';
import { groupMember, vipGroups } from 'helpers/user';
import { useCmsPageVisibility } from 'hooks/useCmsPageVisibility';
import { useCloverGoldAvailable } from 'modules/clover-gold/hooks/useCloverGoldAvailable';
import { useUserCanAccessQuestPromo } from 'modules/common/hooks';
import { usePromoRestrictions } from 'modules/common/hooks/usePromoRestrictions';
import { useAuroraCrystalAvailable } from 'modules/crystal/hooks';
import { useAvailable as useHeadlinePromoAvailable } from 'modules/headlinePromo/hooks/useAvailable';
import { useAvailable as usePumpkinRushAvailable } from 'modules/pumpkinRush/hooks/useAvailable';
import type { FC } from 'react';
import { useContext, useMemo } from 'react';
import { AuroraPanel } from './AuroraPanel';
import { BonanzaPromoSection } from './BonanzaSection';
import { CloverGoldPanel } from './CloverGoldPanel';
import { HeadlinePromoPanel } from './HeadlinePromoPanel';
import { MissionPanel } from './MissionPanel';
import { PumpkinRushPanel } from './PumpkinRushPanel';
import { QuestPanel } from './QuestPanel';
import { StarDustRushPanel } from './StarDustRushPanel';
import { ScPromoPanels } from './styled';

interface PanelProps {
  onLinkClick?: () => void;
}

export const PromoPanelsDesktop = () => {
  const { isBonanzaPromoAvailable } = useContext(BonanzaContext);
  const { user, pendingUser } = useContext(AuthContext);
  const { isStardustRushAvailable, stardustRushCMSData } =
    useContext(StardustRushContext);
  const isStardustRushPageVisible = useCmsPageVisibility('stardustRush');
  const { isMissionDateValid } = useContext(ConfigContext);

  const { hasUserAccess } = usePromoRestrictions();

  const isVip = useMemo(
    () => vipGroups.some((group) => groupMember(group)(user)) && !pendingUser,
    [user, pendingUser]
  );

  const isStardustRush =
    isStardustRushAvailable &&
    stardustRushCMSData &&
    isVip &&
    isStardustRushPageVisible;

  const { isAuroraCrystalVisible } = useAuroraCrystalAvailable();
  const { isAvailable: isCloverGoldAvailable } = useCloverGoldAvailable();
  const { isAvailable: isHeadlinePromoAvailable } = useHeadlinePromoAvailable();
  const { isAvailable: isPumpkinRushAvailable } = usePumpkinRushAvailable();
  const isQuestPromoVisible = useUserCanAccessQuestPromo();
  const isMissionPromoVisible = isMissionDateValid && hasUserAccess;

  const isPanelsDesktop =
    isBonanzaPromoAvailable ||
    isStardustRush ||
    isAuroraCrystalVisible ||
    isQuestPromoVisible ||
    isMissionPromoVisible ||
    isCloverGoldAvailable ||
    isHeadlinePromoAvailable ||
    isPumpkinRushAvailable;

  if (!isPanelsDesktop) return null;

  return (
    <>
      <div className="divider" />

      <ScPromoPanels>
        {isBonanzaPromoAvailable && <BonanzaPromoSection />}
        {isStardustRush && <StarDustRushPanel />}
        {isAuroraCrystalVisible && <AuroraPanel />}
        {isQuestPromoVisible && <QuestPanel />}
        {isMissionPromoVisible && <MissionPanel />}
        {isCloverGoldAvailable && <CloverGoldPanel />}
        {isHeadlinePromoAvailable && <HeadlinePromoPanel />}
        {isPumpkinRushAvailable && <PumpkinRushPanel />}
      </ScPromoPanels>
    </>
  );
};

export const PromoPanelsMobile: FC<PanelProps> = ({ onLinkClick }) => {
  const { isBonanzaPromoAvailable } = useContext(BonanzaContext);
  const { user, pendingUser } = useContext(AuthContext);
  const { isStardustRushAvailable } = useContext(StardustRushContext);
  const { isMissionDateValid } = useContext(ConfigContext);

  const { hasUserAccess } = usePromoRestrictions();

  const isVip = useMemo(
    () => vipGroups.some((group) => groupMember(group)(user)) && !pendingUser,
    [user, pendingUser]
  );
  const isStardustRushPageVisible = useCmsPageVisibility('stardustRush');

  const isStardustRush =
    isStardustRushAvailable && isVip && isStardustRushPageVisible;

  const { isAuroraCrystalVisible } = useAuroraCrystalAvailable();
  const isQuestPromoVisible = useUserCanAccessQuestPromo();
  const { isAvailable: isCloverGoldAvailable } = useCloverGoldAvailable();
  const { isAvailable: isHeadlinePromoAvailable } = useHeadlinePromoAvailable();
  const { isAvailable: isPumpkinRushAvailable } = usePumpkinRushAvailable();

  const isMissionPromoVisible = isMissionDateValid && hasUserAccess;

  const isPanelsMobile =
    isBonanzaPromoAvailable ||
    isStardustRush ||
    isAuroraCrystalVisible ||
    isQuestPromoVisible ||
    isMissionPromoVisible ||
    isCloverGoldAvailable ||
    isHeadlinePromoAvailable ||
    isPumpkinRushAvailable;

  if (!isPanelsMobile) return null;

  return (
    <ScPromoPanels isMobile>
      {isBonanzaPromoAvailable && (
        <BonanzaPromoSection isMobile onLinkClick={onLinkClick} />
      )}
      {isStardustRush && (
        <StarDustRushPanel isMobile onLinkClick={onLinkClick} />
      )}
      {isAuroraCrystalVisible && <AuroraPanel onLinkClick={onLinkClick} />}
      {isQuestPromoVisible && <QuestPanel onLinkClick={onLinkClick} />}
      {isMissionPromoVisible && <MissionPanel onLinkClick={onLinkClick} />}
      {isCloverGoldAvailable && (
        <CloverGoldPanel isMobile onLinkClick={onLinkClick} />
      )}
      {isHeadlinePromoAvailable && (
        <HeadlinePromoPanel isMobile onLinkClick={onLinkClick} />
      )}
      {isPumpkinRushAvailable && (
        <PumpkinRushPanel isMobile onLinkClick={onLinkClick} />
      )}
    </ScPromoPanels>
  );
};
